import { Col, Form, Row } from "antd";
import dayjs from "dayjs";
import React, { useEffect, forwardRef } from "react";
import { FormProvider } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Calendar } from "../../../../assets/svg";
import { AppDispatch } from "../../../../redux/store";
import { DateField, InputField, ReactSelectField } from "../../../atoms/FormElement";
import { NumberOfYearsSelect } from "./NumberOfYearsSelect";
import {
  calculateLeaseEndDate,
  dateFormat,
  datePlaceholderddmmyyFormat,
  disableBookingEndDate,
  disableLeaseStartDate,
  disablePreviousDate,
  disableResidentialLeaseEndDate,
  generateYearOptions,
  rentFreePeriodValues,
} from "./utils";
import Parkings from "./Parkings";
import { setBookingForm } from "../ducks/actions";
import { findSelectOption, getValidationProps } from "../../../libs/form";
import { LeaseTerms, UsageType } from "../../../../types/bookings";
import { useMasterConfigSelect } from "../../../../hooks/useMasterConfigSelect";
import { useGetOrganization } from "../../../../api/v1/organizations/useGetOrganization";
import { InputNumberFieldV2 as InputNumberField } from "../../../atoms/FormElement/InputNumberField";

const index = forwardRef(
  ({
    onSubmit,
    methods,
    errors,
    control,
    setValue,
    handleSubmit,
    watchedFormValues,
    getValues,
    parkingsWatcher,
    leaseEndDateRef,
    isNumberOfYear,
  }: any) => {
    const { bookingForm, landlordUsers, bookingDetails } = useSelector((state: any) => state.newBooking);
    const { leaseEndDate } = (bookingDetails?.leaseTerms || {}) as LeaseTerms;
    const now = dayjs();
    const defaultStart = now.add(14, "day");
    const initialLeaseEndDate = dateFormat(leaseEndDate) || defaultStart.add(1, "year").subtract(1, "day");
    const dispatch = useDispatch<AppDispatch>();

    const { selectOptions: leaseNatureOptions, isSuccessMasterConfig } = useMasterConfigSelect({
      configItem: "leaseNature",
      filterBy: "usageType",
      value: String(bookingDetails?.propertyDetails?.usagesType).toLowerCase() as UsageType,
      isEnabled: Boolean(bookingDetails?.propertyDetails?.usagesType),
    });

    useEffect(() => {
      if (bookingForm?.leaseTerms?.leaseEndDate) {
        leaseEndDateRef.current.actual = dayjs(bookingForm.leaseTerms.leaseEndDate);
      }
    }, [bookingForm?.leaseTerms?.leaseEndDate]);

    useEffect(() => {
      leaseEndDateRef.current.actual = dayjs(bookingDetails?.leaseTerms?.rentFreeStartDate ?? initialLeaseEndDate);
    }, [bookingDetails?.leaseTerms?.rentFreeStartDate]);

    useEffect(() => {
      if (!bookingForm?.leaseTerms?.leaseNature || !isSuccessMasterConfig) return;

      const selectedLeaseNature = findSelectOption(leaseNatureOptions, bookingForm.leaseTerms.leaseNature);
      if (!selectedLeaseNature) return;

      setValue("leaseNature", selectedLeaseNature);
    }, [bookingForm?.leaseTerms?.leaseNature, isSuccessMasterConfig]);

    useEffect(() => {
      bookingForm?.parkings?.length > 0 && setValue("parkings", bookingForm?.parkings);
    }, [bookingForm?.parkings]);

    // Initialize booking form if empty
    useEffect(() => {
      const userDetails = JSON.parse(localStorage.getItem("login-response") || "{}");
      if (!bookingDetails?.leaseTerms) {
        const currentDate = dayjs();
        const leaseStartDate = currentDate.add(14, "day");

        const data = {
          leaseManager: userDetails.userId,
          leaseTerms: {
            moveInDate: leaseStartDate,
            leaseStartDate,
            leaseNature: bookingForm?.leaseTerms?.leaseNature,
            leaseEndDate: leaseStartDate.subtract(1, "day").add(1, "year"),
          },
          bookingTerms: {
            bookingStartDate: currentDate,
            bookingEndDate: currentDate.add(14, "day"),
            ...bookingForm?.bookingTerms,
          },
        };

        if (Object.keys(bookingForm?.leaseTerms || {}).length > 0) {
          data.leaseTerms = { ...data.leaseTerms, ...bookingForm?.leaseTerms };
        }

        dispatch(setBookingForm(data));
      }
    }, [bookingDetails]);

    const { organization } = useGetOrganization();
    const yearsOptions = generateYearOptions({ start: 1, end: 10, withCustomOption: true });
    const isCustomNumberOfYears = watchedFormValues?.numberOfYears?.value === "custom";

    return (
      <>
        <FormProvider {...methods}>
          <Form
            layout="vertical"
            name="bookingform"
            scrollToFirstError
            className="bf-main"
            onFinish={handleSubmit((values) => onSubmit(values))}
          >
            <Col span={24}>
              <Row gutter={24}>
                <Col span={8}>
                  <ReactSelectField
                    control={control}
                    isRequired={true}
                    setValue={setValue}
                    label="Lease Executive"
                    fieldname="leaseManager"
                    selectOption={landlordUsers}
                    iProps={{ placeholder: "Select" }}
                    rules={{ required: "Please select one" }}
                    validate={errors?.leaseManager && "error"}
                    validMessage={errors?.leaseManager?.message}
                  />
                </Col>

                <Col span={8}>
                  <InputField
                    control={control}
                    setValue={setValue}
                    label="External Agent"
                    fieldname="externalAgent"
                    iProps={{ placeholder: "Enter External Agent" }}
                  />
                </Col>

                <Col span={8}>
                  <ReactSelectField
                    isRequired={true}
                    control={control}
                    setValue={setValue}
                    label="Lease Nature"
                    fieldname="leaseNature"
                    selectOption={leaseNatureOptions}
                    iProps={{ placeholder: "Select" }}
                    validate={errors.leaseNature && "error"}
                    rules={{ required: "Please select lease nature" }}
                    validMessage={errors.leaseNature && errors.leaseNature.message}
                  />
                </Col>

                <Col span={8}>
                  <InputNumberField
                    isRequired
                    label="No. of Occupants"
                    fieldname="numberOfOcupants"
                    control={control}
                    defaultValue={1}
                    inputNumberProps={{
                      placeholder: "No. of Occupants",
                      min: 1,
                    }}
                    rules={{ required: "Please enter occupants" }}
                  />
                </Col>
              </Row>

              {/* second row */}
              <Row gutter={24}>
                <Col span={4}>
                  <DateField
                    isRequired={true}
                    control={control}
                    setValue={setValue}
                    label="Booking Start Date"
                    fieldname="bookingStartDate"
                    rules={{ required: "Please select date" }}
                    validate={errors.bookingStartDate && "error"}
                    iProps={{
                      className: "cd",
                      suffixIcon: <Calendar />,
                      disabledDate: disablePreviousDate,
                      format: datePlaceholderddmmyyFormat,
                      placeholder: datePlaceholderddmmyyFormat,
                    }}
                    onChange={(date) => {
                      if (date) {
                        setValue("bookingEndDate", dayjs(date).add(14, "day"));
                        setValue("leaseStartDate", dayjs(date).add(14, "day"));
                        setValue("moveInDate", dayjs(date).add(14, "day"));
                        setValue("leaseEndDate", dayjs(date).add(14, "day").add(1, "year").subtract(1, "day"));
                      } else setValue("bookingEndDate", "");
                    }}
                    validMessage={errors.bookingStartDate && errors.bookingStartDate.message}
                  />
                </Col>

                <Col span={4}>
                  <DateField
                    control={control}
                    isRequired={true}
                    setValue={setValue}
                    label="Booking End date"
                    fieldname="bookingEndDate"
                    rules={{ required: "Please select date" }}
                    validate={errors.bookingEndDate && "error"}
                    iProps={{
                      format: datePlaceholderddmmyyFormat,
                      placeholder: datePlaceholderddmmyyFormat,
                      suffixIcon: <Calendar />,
                      disabledDate: (date) =>
                        disableBookingEndDate(date, getValues("bookingStartDate"), getValues("bookingEndDate")),
                    }}
                    validMessage={errors.bookingEndDate && errors.bookingEndDate.message}
                  />
                </Col>

                <Col span={4}>
                  <DateField
                    control={control}
                    isRequired={true}
                    setValue={setValue}
                    label="Lease Start Date"
                    fieldname="leaseStartDate"
                    validate={errors.leaseStartDate && "error"}
                    rules={{
                      required: "Enter lease start date",
                    }}
                    onChange={(date) => {
                      if (date) {
                        const leaseEndDate = calculateLeaseEndDate(date, 1);

                        leaseEndDateRef.current.actual = leaseEndDate;
                        setValue("leaseEndDate", leaseEndDate);
                        setValue("moveInDate", date);
                        setValue("commencementDate", date);
                      } else {
                        leaseEndDateRef.current.actual = null;
                        setValue("leaseEndDate", null);
                        setValue("moveInDate", null);
                        setValue("commencementDate", null);
                      }

                      setValue("numberOfYears", yearsOptions[0]);
                      setValue("rentFreePeriod", null);
                      leaseEndDateRef.current.withRentFreePeriod = null;
                    }}
                    iProps={{
                      format: datePlaceholderddmmyyFormat,
                      placeholder: datePlaceholderddmmyyFormat,
                      suffixIcon: <Calendar />,
                      disabledDate: (date) =>
                        disableLeaseStartDate(
                          date,
                          getValues("bookingStartDate"),
                          organization?.leaseStartDatePastDaysLimit,
                        ),
                    }}
                    validMessage={errors.leaseStartDate && errors.leaseStartDate.message}
                  />
                </Col>

                <Col span={4}>
                  <NumberOfYearsSelect
                    options={yearsOptions}
                    isDisabled={!watchedFormValues?.leaseStartDate || isNumberOfYear}
                    leaseEndDateRef={leaseEndDateRef}
                  />
                </Col>

                <Col span={4}>
                  <DateField
                    control={control}
                    isRequired={true}
                    setValue={setValue}
                    label="Lease End Date"
                    fieldname="leaseEndDate"
                    disabled={!watchedFormValues?.leaseStartDate || !isCustomNumberOfYears}
                    validate={errors.leaseEndDate && "error"}
                    rules={{
                      required: "Enter lease end date",
                    }}
                    onChange={(date) => {
                      if (date) {
                        leaseEndDateRef.current.actual = date;
                      }
                      setValue("rentFreePeriod", null);
                      leaseEndDateRef.current.withRentFreePeriod = null;
                      // handleLeaseEndDate(
                      //   date,
                      //   getValues("leaseStartDate"),
                      //   setValue
                      // )
                    }}
                    iProps={{
                      format: datePlaceholderddmmyyFormat,
                      placeholder: datePlaceholderddmmyyFormat,
                      suffixIcon: <Calendar />,
                      disabledDate: (date) =>
                        disableResidentialLeaseEndDate(date, getValues("leaseStartDate"), isCustomNumberOfYears),
                    }}
                    validMessage={errors.leaseEndDate && errors.leaseEndDate.message}
                  />
                </Col>

                <Col span={4}>
                  <ReactSelectField
                    control={control}
                    setValue={setValue}
                    label="Rent Free Period"
                    fieldname="rentFreePeriod"
                    isDisabled={!watchedFormValues?.leaseStartDate}
                    selectOption={rentFreePeriodValues()}
                    onChange={(e) => {
                      const extendedEndDate = dayjs(leaseEndDateRef.current.actual).add(parseInt(e.value), "days");
                      setValue("leaseEndDate", extendedEndDate, { shouldDirty: true, shouldValidate: true });
                      leaseEndDateRef.current.withRentFreePeriod = extendedEndDate;
                    }}
                    iProps={{ placeholder: "Select" }}
                  />
                </Col>
              </Row>

              {/* third row */}
              <Row gutter={24}>
                <Col span={4}>
                  <DateField
                    control={control}
                    label="Rent Commencement Date"
                    fieldname="commencementDate"
                    disabled={!watchedFormValues?.leaseStartDate}
                    iProps={{ format: datePlaceholderddmmyyFormat, placeholder: datePlaceholderddmmyyFormat }}
                    {...getValidationProps({ fieldName: "commencementDate", errors, isRequired: true })}
                  />
                </Col>
                <Col span={4}>
                  <DateField
                    control={control}
                    isRequired={true}
                    setValue={setValue}
                    label="Move-In Date"
                    fieldname="moveInDate"
                    disabled={!watchedFormValues?.leaseStartDate}
                    rules={{ required: "Enter Date" }}
                    validate={errors?.moveInDate && "error"}
                    validMessage={errors?.moveInDate && errors?.moveInDate.message}
                    iProps={{
                      format: datePlaceholderddmmyyFormat, // datePlaceholderddmmyyFormat
                      placeholder: datePlaceholderddmmyyFormat,
                      suffixIcon: <Calendar />,
                    }}
                  />
                </Col>

                <Col span={4}>
                  <InputNumberField
                    label="Number of parking slots"
                    fieldname="noOfFreeParkingSlots"
                    control={control}
                  />
                </Col>
              </Row>
              {parkingsWatcher && (
                <Row gutter={24}>
                  <Col span={24} style={{ marginTop: "20px" }}>
                    <p className="ft-main-label">Parking</p>
                    <Parkings
                      data={parkingsWatcher}
                      setParkingValue={(data) => setValue("parkings", data)}
                      noOfFreeParkingSlots={watchedFormValues?.noOfFreeParkingSlots}
                    />
                  </Col>
                </Row>
              )}
            </Col>
          </Form>
        </FormProvider>
      </>
    );
  }
);

export default index;
